import _, { compact, first, isNil, last, uniq } from "lodash"
import { get } from "lodash"
import { ImdfFeature } from "providers/venue/types"
import { isValidTime } from "./time"
import dayjs from "config/dayjs"
import { Dayjs } from "dayjs"
import { IConfigIcon } from "./icon"
import {
  Id,
  IExternalLink,
  ILocation,
  IMedia,
  LevelData,
  MediaUrl,
  ProductData,
  SectionData,
} from "providers/venue/types"
import { createLetterNameFirstComparator } from "./sortOccupantLettersFirst"
import { getIconPathString } from "components/icons/imdf-category-icon-paths"
import { DEFAULT_VENUE_FILTER } from "constant"

// CUSTOM INTERFACE/TYPE
export interface IMultiLanguageInfo {
  en?: string
  th?: string
  zh?: string
}

interface IFeatureDetail {
  id: number | string
  group?: object | string | string[] // Inferred types from the use of i18n's t function
  name: IMultiLanguageInfo | string
  category?: IMultiLanguageInfo[] | string | string[] | null
  level?: IMultiLanguageInfo | string // Inferred types from usage below
  locationDescription?: string | null
  zone?: object | string // Inferred types from usage below
  isAd?: boolean
  boothName?: string
  isNavigatable?: boolean
  location?: Location
  venueLogo?: string
  sectionName?: IMultiLanguageInfo | null
}
export type Location = null | IMultiLanguageInfo | string

// FUNCTION RETURN INTERFACE
export interface IAmenityDetail extends IFeatureDetail {
  description: IMultiLanguageInfo
  location: Location
  venueLogoSimple?: MediaUrl
  venueLogo?: MediaUrl
  venueRef: string
  logo?: MediaUrl
  venueName?: string
  hours: string | null
  allLevels: string[]
  hasLocation?: boolean
}
export interface IOpeningDetail extends IFeatureDetail {
  venueLogoSimple?: MediaUrl
  venueLogo?: MediaUrl
  venueRef: string
}

interface IUnitDetail extends IFeatureDetail {
  location: Location
  venueLogo?: MediaUrl
}

export interface IEventDetail extends IFeatureDetail {
  description: IMultiLanguageInfo
  coverImage?: MediaUrl
  location: Location
  gallery?: IMedia[] | null
  allImages?: IMedia[] | null
  startTime: Dayjs
  endTime: Dayjs
  startDate: Dayjs | null
  endDate: Dayjs | null
  showAlways: boolean
  showInfo: boolean
  locationId: string
  hasLocation: boolean
  websiteLink: string
  venueName?: string
  venueLogo: MediaUrl
  status: StatusByDateRange
}

export interface IPromotionDetail extends IFeatureDetail {
  description: IMultiLanguageInfo
  coverImage?: MediaUrl
  location: Location
  gallery?: IMedia[] | null
  allImages?: IMedia[] | null
  startTime: Dayjs
  endTime: Dayjs
  startDate: Dayjs | null
  endDate: Dayjs | null
  showAlways: boolean
  showInfo: boolean
  locationId: string
  hasLocation: boolean
  websiteLink: string
  venueName?: string
  venueLogo: MediaUrl
  occupantDetail?: IOccupantDetail
  status: StatusByDateRange
}

export interface IPrivilegeDetail extends Partial<IFeatureDetail> {
  description: IMultiLanguageInfo
  locationId: string
  hasLocation: boolean
  venueName?: string
  venueLogo: MediaUrl
  occupantDetail?: IOccupantDetail | null
}
export interface IPostDetail extends Partial<IFeatureDetail> {
  id: Id
  title: ILocale
  type: string
  category?: string
  categoryRef?: string
  categoryIconPath?: string
  categoryIconImage?: string
  description?: ILocale
  gallery?: MediaUrl[] | null
  featureImage?: MediaUrl
  coverImage?: string
  allImages?: MediaUrl[]
  order: number
  locationId?: Id
  locationName?: Location
  locationUrl?: string
  locationLinkInfo?: ILocation
  isFeatured: boolean
  hasLocation: boolean
  venueLogo?: MediaUrl
  locationDetail?: IOccupantDetail | IAmenityDetail | null
  externalLink?: string
  startDate?: Dayjs | null
  endDate?: Dayjs | null
  publishedAt?: Dayjs
  venueName?: string
}
interface IKioskDetail extends IFeatureDetail {
  location: Location
}

export interface IVenueDetail extends IFeatureDetail {
  logoSimple: MediaUrl
  logo: MediaUrl
  reference: string
}

export interface IOccupantDetail extends IFeatureDetail {
  logo?: MediaUrl
  featuredImage?: MediaUrl
  coverImage?: MediaUrl
  venueLogoSimple: MediaUrl
  venueLogo: MediaUrl
  venueRef: string
  phone: string
  hours: string | null
  websiteLink: string
  gallery?: IMedia[]
  privileges: ImdfFeature[]
  description: IMultiLanguageInfo | string
  location: Location
  isFeatured: boolean
  categoryIconPath: string
  subCategory: string
  categoryIconImage?: MediaUrl
  categoryIcon?: IConfigIcon
  subCategoryIcon?: IConfigIcon
  maintenance?: IMaintenaceDetail | null
  isMaintenanceRecord?: boolean
  venueName?: string
  products?: ProductData[]
  reservations?: IExternalLink[]
  canReserve?: boolean
  startDate: Dayjs
  endDate: Dayjs
  allLevels: string[]
  hasMultipleLocations?: boolean
  cardStyle: string
  hasLocation: boolean
}

export interface IMaintenaceDetail {
  id: number | string
  enable: boolean
  tempLocation?: ImdfFeature | null
  tempLocationName?: string
  hasTempLocation: boolean
  maintenaceStart?: Dayjs | null
  maintenaceEnd?: Dayjs | null
}

interface IGeolocationDetail extends IFeatureDetail {
  venue: ImdfFeature
  venueLogo: MediaUrl
  venueName?: string
}

export interface ILevelDetail extends IFeatureDetail {
  ordinal: number
  altName?: IMultiLanguageInfo | string
}

export interface ISectionDetail extends IFeatureDetail {
  logo?: MediaUrl
  venueLogo: MediaUrl
  coverImage?: MediaUrl
  featuredImage?: MediaUrl
  gallery?: IMedia[]
  isFeatured: boolean
  hours: string | null
  description?: IMultiLanguageInfo | string
  venueName?: IMultiLanguageInfo | string
  parents: SectionData[] | null
  altName?: IMultiLanguageInfo | string
}

interface ITaxonomyDetail extends IFeatureDetail {}

export interface IParsedListItem {
  id: string | number
  avatarProps: {
    src?: string
    icon: IConfigIcon
  }
  primary?: string
  secondary?: string
  name?: IMultiLanguageInfo | string
  properties?: Record<string, any> | null
  venueRef?: string
  lastScanPrimaryText?: string
  lastScanSecondaryText?: string
  categoryText?: string
  floorText?: string
  zoneText?: string
  boothNameText?: string
  groupText?: string
  venueLogo?: string
  sectionName?: IMultiLanguageInfo | string
}

// FUNCTION TYPE DEFINITION
// GetFeatureLevel
type ParseLevelText = <T extends ImdfFeature = ImdfFeature>(
  feature: T,
  lang?: string
) => string

type GetFeatureId = (
  feature: ImdfFeature,
  defaultValue?: string | number
) => string | number

type GetFeatureName = <T extends ImdfFeature = ImdfFeature>(
  feature: T,
  defaultValue?: string
) => IMultiLanguageInfo | string

type GetFeatureOrdinalResult = number | undefined | null
type GetFeatureOrdinal = <
  T = ImdfFeature,
  TDefaultValue = GetFeatureOrdinalResult
>(
  feature: T,
  defaultValue?: TDefaultValue
) => GetFeatureOrdinalResult
type GetFeatureLevel = <T = ImdfFeature, TDefaultValue = any>(
  feature: T,
  defaultValue?: TDefaultValue
) => LevelData

type GetFeatureDescription = (
  feature: ImdfFeature,
  defaultValue?: string
) => IMultiLanguageInfo | string

type GetCategoryName = (feature: ImdfFeature) => string | string[]

type GetAmenityDetail = (feature: ImdfFeature) => IAmenityDetail

type GetOpeningDetail = (feature: ImdfFeature) => IOpeningDetail

type GetUnitDetail = (feature: ImdfFeature) => IUnitDetail

type GetEventDetail = (feature: ImdfFeature) => IEventDetail

type GetPromotionDetail = (feature: ImdfFeature) => IPromotionDetail

type GetPrivilegeDetail = (feature: ImdfFeature) => IPrivilegeDetail

type GetPostDetail = (feature: ImdfFeature) => IPostDetail

type GetKioskDetail = (feature: ImdfFeature) => IKioskDetail

type GetVenueDetail = (venue: ImdfFeature) => IVenueDetail

type GetOccupantDetail = (occupant: ImdfFeature) => IOccupantDetail

type GetSectionDetail = (section: ImdfFeature) => ISectionDetail

type GetMaintenanceDetail = (occupant: ImdfFeature) => IMaintenaceDetail | null

type GetGeolocationDetail = (geolocation: ImdfFeature) => IGeolocationDetail

type GetLevelDetail = (level: ImdfFeature) => ILevelDetail

type GetVenueLevels = (
  sortedLevels: LevelData[],
  viewingVenueId?: string,
  isMultiVenueProject?: boolean
) => LevelData[]

type FilterLevelsByFeatureOrdinals = (
  levels: LevelData[],
  features: ImdfFeature[]
) => LevelData[]

type GetTaxonomyDetail = (taxonomy: ImdfFeature) => ITaxonomyDetail

type GetLocationNameFromFeature = (feature: ImdfFeature) => Location

export type GetFeatureDetailResult =
  | IAmenityDetail
  | IEventDetail
  | IUnitDetail
  | IKioskDetail
  | IOccupantDetail
  | IPromotionDetail
  | IPrivilegeDetail
  | ILevelDetail
  | ITaxonomyDetail
  | IVenueDetail
  | IGeolocationDetail
  | IPostDetail
  | ISectionDetail
  | null

type GetFeatureDetail = (feature: ImdfFeature) => GetFeatureDetailResult

type ListItemPropsParserFactoryParams = ({
  t,
  hideLevel,
  hideVenueLogo,
  newShopConfig,
}: {
  t: any
  hideLevel: boolean
  hideVenueLogo: boolean
  newShopConfig?: object
}) => ListItemPropsParserFactory
type ListItemPropsParserFactory = (feature: ImdfFeature) => IParsedListItem
interface SortingConfigFeatureByName {
  field: string
  order: "asc" | "desc"
  strategy?: string
  compare?: (a: ImdfFeature, b: ImdfFeature) => number
}
type CreateSortingConfigFeatureByName = (
  chosenLanguage: string
) => SortingConfigFeatureByName[]

export type CreateSortingConfigByOrdinalAndName = (
  chosenLanguage: string
) => SortingConfigFeatureByName[]

type CreateSortingConfigFeatureByNameLettersFirst = (
  chosenLanguage: string
) => SortingConfigFeatureByName[]

type GetCategoryIcon = (category: ImdfFeature) => {
  image?: string | null
  icon_path?: string | null
}

export const parseLevelText: ParseLevelText = (feature, lang = "en") => {
  if (!feature?.properties?.level && !feature?.properties?.level_name) return ""

  const { level, level_name } = feature.properties
  const levelName = level?.properties?.name.en || level_name

  return levelName
}

export const getFeatureId: GetFeatureId = (feature, defaultValue = "") =>
  get(feature, "id", defaultValue)

export const getFeatureName: GetFeatureName = (feature, defaultValue = "") => {
  return get(feature, "properties.name") || defaultValue
}
export const getFeatureOrdinal: GetFeatureOrdinal = (feature, defaultValue) => {
  const ordinal = get(feature, "properties.ordinal")
  return (!isNil(ordinal) ? ordinal : defaultValue) as GetFeatureOrdinalResult
}
export const getFeatureLevel: GetFeatureLevel = (feature, defaultValue) => {
  return (get(feature, "properties.level") || defaultValue) as LevelData
}
export const getFeatureDescription: GetFeatureDescription = (
  feature,
  defaultValue = ""
) => get(feature, "properties.description", defaultValue)

export const getCategoryName: GetCategoryName = (feature) => {
  const { properties, feature_type } = feature
  switch (feature_type) {
    case "amenity": {
      return `${feature_type}.${properties.category}`
    }
    case "occupant": {
      const localParentCategory = get(
        feature,
        "properties.local_parent_categories"
      )
      return localParentCategory.map((category: ImdfFeature) =>
        get(category, "properties.name", "")
      )
    }
    default:
      return ""
  }
}

export enum StatusByDateRange {
  Upcoming = "upcoming",
  Ongoing = "ongoing",
  Expired = "expired",
}

export const getStatusByDateRange = (
  start: Dayjs | string,
  end: Dayjs | string
): StatusByDateRange | null => {
  const now = dayjs()
  const startDate = dayjs(start)
  const endDate = dayjs(end)

  if (!startDate.isValid() || !endDate.isValid()) return null

  if (now.isBefore(startDate)) return StatusByDateRange.Upcoming
  if (now.isSameOrAfter(startDate) && now.isBefore(endDate))
    return StatusByDateRange.Ongoing
  return StatusByDateRange.Expired
}

const getAmenityDetail: GetAmenityDetail = (feature) => {
  const { feature_type, properties } = feature
  const {
    units,
    category,
    venue,
    description,
    hours,
    level_name,
    amenity_logo: amenityLogo,
    isAd = false,
    isNavigatable,
    section,
  } = feature?.properties || {}
  const gallery = get(properties, "gallery")
  const logo =
    get(amenityLogo, "formats.thumbnail.url") || get(amenityLogo, "url")
  const categoryName = `${feature_type}.${category}`
  const nameObject = getFeatureName(feature)
  const nameText = _.isEmpty(nameObject) ? categoryName : nameObject
  const zoneName = get(units, "[0].properties.section.properties.name", "")
  let locationDescription = get(feature, "properties.location_description", "")
  locationDescription = !_.isEmpty(locationDescription)
    ? locationDescription
    : null

  const locationName = getLocationNameFromFeature(feature)

  const venueProperties = get(venue, "properties")
  const venueLogoSimple =
    venueProperties?.logo_simple?.formats?.thumbnail?.url ||
    venueProperties?.logo_simple?.url
  const venueLogo =
    venueProperties?.logo?.formats?.thumbnail?.url || venueProperties?.logo?.url
  const venueRef = venueProperties?.reference
  const venueName = get(venueProperties, "name")
  // for amenity that is placed outside units
  // always check if Amenity is binding to unit or not because amenity without units can't be navigated to
  // isNavigatable === null -> check unit
  // isNavigatavle !== null -> check unit and isNavigatable for case like that unit is on maintenance
  const isAmenityAccessible = _.isNil(isNavigatable)
    ? !!units
    : isNavigatable && !!units
  const sectionProperties = get(section, "properties")
  const sectionName = get(sectionProperties, "name")

  return {
    id: getFeatureId(feature),
    name: nameText,
    level: level_name,
    zone: zoneName,
    category: categoryName,
    location: locationName,
    description,
    locationDescription: locationDescription,
    venueLogoSimple,
    venueLogo,
    venueRef,
    gallery,
    hours,
    isAd,
    logo,
    isNavigatable: isAmenityAccessible,
    allLevels: getAllLevelsFromFeature(feature),
    venueName,
    sectionName,
    hasLocation: isAmenityAccessible,
  }
}

const getOpeningDetail: GetOpeningDetail = (feature) => {
  const { feature_type } = feature
  const {
    level,
    category,
    venue,
    alt_name: altName,
  } = feature?.properties || {}

  const categoryName = `${feature_type}.${category}`
  const levelName = get(level, "name")
  const nameObject = getFeatureName(feature)
  const nameText = _.isEmpty(nameObject) ? altName || categoryName : nameObject
  const venueProperties = get(venue, "properties")
  const venueLogoSimple =
    venueProperties?.logo_simple?.formats?.thumbnail?.url ||
    venueProperties?.logo_simple?.url
  const venueLogo =
    venueProperties?.logo?.formats?.thumbnail?.url || venueProperties?.logo?.url
  const venueRef = venueProperties?.reference

  return {
    id: getFeatureId(feature),
    name: nameText,
    level: levelName,
    category: categoryName,
    venueLogoSimple,
    venueLogo,
    venueRef,
  }
}

const getUnitDetail: GetUnitDetail = (feature) => {
  const { name, level, section, venue } = feature?.properties || {}
  const levelName = getFeatureName(level)
  const zoneName = getFeatureName(section)
  const locationName = getLocationNameFromFeature(feature)
  const venueLogo =
    get(venue, "properties.logo.formats.thumbnail.url") ||
    get(venue, "properties.logo.url")

  return {
    name,
    level: levelName,
    zone: zoneName,
    id: getFeatureId(feature),
    location: locationName,
    venueLogo,
  }
}
export const getEventDetail: GetEventDetail = (feature) => {
  const {
    name,
    description,
    cover_image,
    gallery,
    website_link: websiteLink,
    feature_id: locationId,
    feature: locationFeature,
    start_date,
    end_date,
    show_always: showAlways,
    daily_start_time,
    daily_end_time,
    venue,
    project,
    isAd = false,
  } = feature?.properties || {}
  const venueProperties = get(venue, "properties")
  const locationName = getLocationNameFromFeature(feature)
  const coverImage = cover_image?.url
  const allImages = compact([cover_image, ...(gallery || [])])
  const levelName = parseLevelText(feature.properties.feature)
  const startTime =
    !showAlways && isValidTime(daily_start_time)
      ? daily_start_time.slice(0, 5)
      : null
  const endTime =
    !showAlways && isValidTime(daily_end_time)
      ? daily_end_time.slice(0, 5)
      : null
  const startDate = showAlways ? null : dayjs(start_date)
  const endDate = showAlways ? null : dayjs(end_date)
  const status = getStatusByDateRange(startDate, endDate)
  const venueName = venueProperties?.name
  const venueLogo =
    venueProperties?.logo?.formats?.thumbnail?.url || venueProperties?.logo?.url
  const boothName =
    get(feature, "properties.feature.properties.kiosk.properties.name") ||
    get(feature, "properties.feature.properties.unit.properties.name") ||
    " "
  // cover_image
  return {
    name,
    description,
    coverImage,
    id: getFeatureId(feature),
    location: locationName,
    gallery,
    allImages,
    level: levelName,
    websiteLink,
    locationId,
    hasLocation: !!locationFeature,
    startDate,
    endDate,
    showAlways: !!showAlways,
    showInfo: !showAlways || !!locationFeature,
    startTime,
    endTime,
    venueName,
    venueLogo,
    isAd,
    boothName,
    status,
    project,
  }
}

export const getPromotionDetail: GetPromotionDetail = (feature) => {
  const {
    name,
    description,
    cover_image,
    gallery,
    website_link: websiteLink,
    feature_id: locationId,
    feature: locationFeature,
    start_date,
    end_date,
    show_always: showAlways,
    daily_start_time,
    daily_end_time,
    venue,
    isAd = false,
    category,
    project,
  } = feature?.properties || {}
  const venueProperties = get(venue, "properties")

  const locationName = getLocationNameFromFeature(feature)
  const coverImage = cover_image?.url
  const allImages = compact([cover_image, ...(gallery || [])])
  const levelName = parseLevelText(feature.properties.feature)
  const startTime =
    !showAlways && isValidTime(daily_start_time)
      ? daily_start_time.slice(0, 5)
      : null
  const endTime =
    !showAlways && isValidTime(daily_end_time)
      ? daily_end_time.slice(0, 5)
      : null
  const startDate = showAlways ? null : dayjs(start_date)
  const endDate = showAlways ? null : dayjs(end_date)
  const status = getStatusByDateRange(startDate, endDate)
  const venueName = venueProperties?.name
  const venueLogo =
    venueProperties?.logo?.formats?.thumbnail?.url || venueProperties?.logo?.url
  const boothName =
    get(feature, "properties.feature.properties.kiosk.properties.name") ||
    get(feature, "properties.feature.properties.unit.properties.name") ||
    ""

  return {
    name,
    description,
    coverImage,
    category,
    id: getFeatureId(feature),
    location: locationName,
    gallery,
    allImages,
    level: levelName,
    websiteLink,
    locationId,
    hasLocation: !!locationFeature,
    occupantDetail: locationFeature ? getOccupantDetail(locationFeature) : null,
    startDate,
    endDate,
    showAlways: !!showAlways,
    showInfo: !showAlways || !!locationFeature,
    startTime,
    endTime,
    venueName,
    venueLogo,
    status,
    isAd,
    boothName,
    project,
  }
}

export const getPrivilegeDetail: GetPrivilegeDetail = (feature) => {
  const {
    name,
    description,
    occupant_id: locationId,
    occupant: locationFeature,
    venue,
  } = feature?.properties || {}
  const venueProperties = get(venue, "properties")

  const locationName = getLocationNameFromFeature(locationFeature)
  const venueName = venueProperties?.name
  const venueLogo =
    venueProperties?.logo?.formats?.thumbnail?.url || venueProperties?.logo?.url

  return {
    name,
    description,
    id: getFeatureId(feature),
    location: locationName,
    locationId,
    hasLocation: !!locationFeature,
    occupantDetail: locationFeature ? getOccupantDetail(locationFeature) : null,
    venueName,
    venueLogo,
  }
}

export const getPostDetail: GetPostDetail = (feature) => {
  const {
    title,
    description,
    type,
    order,
    feature_image: featureImage,
    cover_image: coverImage,
    gallery = [],
    location: locationLinkInfo = null,
    feature: locationFeature,
    is_featured: isFeatured,
    external_link: externalLink,
    start_date,
    end_date,
    venue,
    publishedAt,
    category,
  } = feature?.properties || {}

  const id = getFeatureId(feature)
  const locationId = getFeatureId(locationFeature)
  const locationName =
    locationLinkInfo?.label || getLocationNameFromFeature(locationFeature)
  const levelName = parseLevelText(locationFeature)
  const venueProperties = get(venue, "properties")
  const venueLogo =
    venueProperties?.logo?.formats?.thumbnail?.url || venueProperties?.logo?.url
  const locationUrl = locationLinkInfo?.url
  const startDate = start_date ? dayjs(start_date) : null
  const endDate = end_date ? dayjs(end_date) : null
  const allImages = compact([featureImage, ...(gallery || [])])
  const categoryName = get(category, "properties.name")
  const categoryRef = get(category, "properties.reference")
  const categoryIconPath = get(category, "properties.icon_path")
  const categoryIconImage = get(category, "properties.icon_image")
  const venueName = get(venueProperties, "name")

  return {
    id,
    title,
    description,
    category: categoryName,
    categoryRef,
    categoryIconPath,
    categoryIconImage,
    type,
    order,
    featureImage,
    coverImage,
    gallery,
    allImages,
    locationId,
    location: locationName,
    level: levelName,
    locationUrl,
    locationLinkInfo,
    externalLink,
    startDate,
    endDate,
    isFeatured,
    hasLocation: !!locationFeature,
    venueLogo,
    venueName,
    locationDetail: locationFeature
      ? (getFeatureDetail(locationFeature) as IOccupantDetail | IAmenityDetail)
      : null,
    publishedAt: publishedAt ? dayjs(publishedAt) : null,
  }
}

const getKioskDetail: GetKioskDetail = (feature) => {
  const { name, level, section } = feature?.properties || {}
  const zoneName = get(section, "properties.name") || ""
  const levelName = getFeatureName(level)
  const locationName = getLocationNameFromFeature(feature)

  return {
    name,
    zone: zoneName,
    level: levelName,
    id: getFeatureId(feature),
    location: locationName,
  }
}

export const getVenueDetail: GetVenueDetail = (venue) => {
  const {
    name,
    logo: logoField,
    logo_simple: logoSimpleField,
    reference,
  } = venue?.properties || {}

  const logoSimple =
    logoSimpleField?.formats?.thumbnail?.url || logoSimpleField?.url
  const logo = logoField?.formats?.thumbnail?.url || logoField?.url

  return {
    id: getFeatureId(venue),
    name,
    logoSimple,
    logo,
    reference,
  }
}

export const getMaintenanceDetail: GetMaintenanceDetail = (occupant) => {
  const { id, properties } = get(
    occupant,
    "properties.maintenance",
    {}
  ) as ImdfFeature
  const {
    is_maintenance: enable,
    maintenance_start_date: maintenanceStartDate,
    maintenance_end_date: maintenanceEndDate,
    unit,
    kiosk,
  } = properties || {}

  if (!enable) return null

  const tempLocationName =
    get(unit, "properties.name") || get(kiosk, "properties.name")
  const tempLocation = unit || kiosk

  return {
    enable,
    id,
    tempLocation,
    hasTempLocation: !!tempLocation,
    tempLocationName,
    maintenaceStart: maintenanceStartDate ? dayjs(maintenanceStartDate) : null,
    maintenaceEnd: maintenanceEndDate ? dayjs(maintenanceStartDate) : null,
  }
}

export const getAllLevelsFromFeature = (feature: any): string[] => {
  const { unit, kiosk, units = [], kiosks = [] } = feature?.properties || {}

  const extractLevel = (level: any): string => {
    return level?.en ?? level ?? null
  }

  return _([
    unit?.properties?.level,
    kiosk?.properties?.level,
    ..._.map(units, "properties.level"),
    ..._.map(kiosks, "properties.level"),
  ])
    .compact()
    .map((level: any) => ({
      name: extractLevel(getFeatureName(level)),
      ordinal: level.properties?.ordinal,
    }))
    .filter((item: any) => item.name)
    .uniqBy("name")
    .sortBy("ordinal")
    .map("name")
    .value()
}

export const getOccupantDetail: GetOccupantDetail = (occupant) => {
  const { properties } = occupant
  const {
    name,
    local_parent_categories,
    local_sub_categories,
    logo,
    featured_image,
    cover_image,
    level_name,
    anchor,
    hours,
    phone,
    groups,
    venue,
    section,
    is_featured: isFeatured,
    website_link: websiteLink,
    is_maintenance,
    is_maintenance_record: isMaintenanceRecord,
    reservations = [],
    can_reserve: canReserve,
    display_settings: displaySettings = {},
    start_date,
    end_date,
    isAd = false,
    hasMultipleLocations,
  } = occupant?.properties || {}

  const unitCoords = get(properties, "unit.geometry.coordinates", [])
  const kioskCoords = get(properties, "kiosk.geometry.coordinates", [])
  const hasLocation =
    (Array.isArray(unitCoords) && unitCoords.length > 0) ||
    (Array.isArray(kioskCoords) && kioskCoords.length > 0)

  const venueProperties = get(venue, "properties")
  const sectionProperties = get(section, "properties")
  const firstParentCategory = first(local_parent_categories)
  const firstSubCategory = first(local_sub_categories)
  const categoryIconPath = get(firstParentCategory, "properties.icon_path")
  const categoryIconImage = get(firstParentCategory, "properties.icon_image")
  const categoryName = get(firstParentCategory, "properties.name")
  const subcategoryName = get(firstSubCategory, "properties.name")
  const firstGroup = first(groups)
  const groupName = get(firstGroup, "properties.name")
  const zoneName = get(anchor, "properties.section.properties.name") || ""
  const logoImage = get(logo, "formats.thumbnail.url") || get(logo, "url")
  const featuredImage =
    get(featured_image, "formats.small.url") || get(featured_image, "url")
  const coverImage =
    get(cover_image, "formats.large.url") || get(cover_image, "url")
  const venueLogoSimple =
    get(venueProperties, "logo_simple.formats.thumbnail.url") ||
    get(venueProperties, "logo_simple.url")
  const venueLogo =
    get(venueProperties, "logo.formats.thumbnail.url") ||
    get(venueProperties, "logo.url")
  const sectionLogo =
    get(sectionProperties, "logo.formats.thumbnail.url") ||
    get(sectionProperties, "logo.url")
  const venueRef = get(venueProperties, "reference")
  const gallery = get(properties, "gallery")
  const privileges = get(properties, "privileges")
  const description = getFeatureDescription(occupant)
  const locationName = getLocationNameFromFeature(occupant)
  const boothName =
    get(properties, "kiosk.properties.name") ||
    get(properties, "unit.properties.name")
  const venueName = get(venueProperties, "name")
  const maintenance = getMaintenanceDetail(occupant)
  const startDate = dayjs(start_date)
  const endDate = dayjs(end_date)
  const cardStyle = get(displaySettings, "card_style", "default")
  const sectionName = get(sectionProperties, "name")

  return {
    id: getFeatureId(occupant),
    venueName,
    zone: zoneName,
    logo: logoImage,
    featuredImage,
    coverImage,
    category: categoryName,
    subCategory: subcategoryName,
    categoryIcon: getCategoryIcon(firstParentCategory as ImdfFeature),
    subCategoryIcon: getCategoryIcon(firstSubCategory as ImdfFeature),
    categoryIconPath,
    categoryIconImage,
    venueLogoSimple,
    venueLogo,
    sectionLogo,
    venueRef,
    phone,
    level: level_name,
    name,
    hours,
    websiteLink,
    gallery,
    privileges,
    group: groupName,
    description: description,
    location: locationName,
    isFeatured,
    maintenance,
    boothName,
    reservations,
    canReserve,
    startDate,
    endDate,
    isAd,
    isMaintenanceRecord,
    allLevels: getAllLevelsFromFeature(occupant),
    hasMultipleLocations,
    displaySettings,
    cardStyle,
    sectionName,
    hasLocation: hasLocation && !is_maintenance,
  }
}
const getGeolocationDetail: GetGeolocationDetail = (geolocation) => {
  const { name, level_name, venue, section } = geolocation?.properties || {}
  const venueProperties = get(venue, "properties")
  const venueLogo =
    get(venueProperties, "logo.formats.thumbnail.url") ||
    get(venueProperties, "logo.url")
  const venueName = get(venueProperties, "name")
  const sectionProperties = get(section, "properties")
  const sectionName = get(sectionProperties, "name")

  return {
    id: getFeatureId(geolocation),
    name,
    level: level_name,
    venue,
    venueLogo,
    venueName,
    sectionName,
  }
}

export const getSectionDetail: GetSectionDetail = (section) => {
  const { properties } = section ?? {}
  const {
    name,
    cover_image,
    logo,
    hours,
    venue,
    local_parent_categories,
    level,
    is_featured: isFeatured,
    featured_image,
    parents,
    alt_name: altName,
  } = properties || {}

  const levelName = level?.properties?.name?.en
  const venueName = getFeatureName(venue)
  const categoryIconPath = local_parent_categories?.[0]?.properties?.icon_path
  const categoryName = local_parent_categories?.[0]?.properties.name

  const venueProperties = venue?.properties
  const venueLogo =
    venueProperties?.logo?.formats?.thumbnail?.url || venueProperties?.logo?.url
  const description = getFeatureDescription(section)
  const coverImage =
    get(cover_image, "formats.large.url") || get(cover_image, "url")
  const logoImage = get(logo, "formats.thumbnail.url") || get(logo, "url")
  const featuredImage =
    get(featured_image, "formats.thumbnail.url") || get(featured_image, "url")
  const gallery = get(properties, "gallery")

  return {
    id: getFeatureId(section),
    name,
    description,
    level: levelName,
    logo: logoImage,
    hours,
    venueName,
    category: categoryName,
    categoryIconPath,
    venueLogo,
    isFeatured,
    coverImage,
    featuredImage,
    gallery,
    parents: parents ?? null,
    altName,
    allLevels: [levelName],
  }
}

export const getLevelDetail: GetLevelDetail = (level) => {
  const { properties } = level
  const { name, ordinal, alt_name: altName } = properties || {}
  return {
    id: getFeatureId(level),
    name,
    ordinal,
    altName,
  }
}

// Levels of the viewing venue, deduped across venues by floor name.
export const getVenueLevels: GetVenueLevels = (
  sortedLevels = [],
  viewingVenueId,
  isMultiVenueProject
) => {
  if (!isMultiVenueProject) return sortedLevels

  return _(sortedLevels)
    .filter(
      (level) =>
        viewingVenueId === DEFAULT_VENUE_FILTER ||
        get(level, "properties.venue_id") === viewingVenueId
    )
    .uniqBy("properties.name.en")
    .value()
}

// Levels that have at least one of the given features on them (matched by ordinal).
export const filterLevelsByFeatureOrdinals: FilterLevelsByFeatureOrdinals = (
  levels = [],
  features = []
) => {
  const ordinals = uniq(features.map((feature) => getFeatureOrdinal(feature)))
  return levels.filter((level) => ordinals.includes(getFeatureOrdinal(level)))
}

export const getTaxonomyDetail: GetTaxonomyDetail = (taxonomy) => {
  const { properties } = taxonomy
  const { name } = properties || {}
  return { id: getFeatureId(taxonomy), name }
}

export interface ITransformLocationNamesByFeaturesOptions {
  separator?: string
}
export type TransformLocationNamesByFeatures = (
  features: ImdfFeature[],
  options?: ITransformLocationNamesByFeaturesOptions
) => Location

export const transformLocationNamesByFeatures: TransformLocationNamesByFeatures =
  (features, { separator = ", " } = {}) => {
    const featureNames = compact(uniq(features.map((v) => getFeatureName(v))))

    // Accumulate grouped location names by locale
    const groupedLocationNames = featureNames.reduce((acc, nameObj) => {
      // Loop over the keys of nameObj (locales)
      Object.keys(nameObj).forEach((locale) => {
        const name = nameObj[locale] ?? null

        // Initialize the array for the locale if it doesn't exist
        if (!acc[locale]) acc[locale] = []

        // Push the name into the corresponding locale array
        acc[locale].push(name)
      })

      return acc
    }, {})

    // After collecting all names for the locale, join them with a comma
    Object.keys(groupedLocationNames).forEach((locale) => {
      groupedLocationNames[locale] =
        groupedLocationNames[locale].join(separator)
    })

    return groupedLocationNames
  }

export const getLocationNameFromFeature: GetLocationNameFromFeature = (
  feature
) => {
  const { feature_type } = feature || {}
  const properties = get(feature, "properties")

  switch (feature_type) {
    case "unit":
      return getFeatureName(get(properties, "section"))
    case "kiosk":
      return getFeatureName(
        get(properties, "unit.properties.section") ||
          get(properties, "anchor.properties.unit.properties.section")
      )
    case "amenity":
      return getFeatureName(get(properties, "units[0]"))
    case "occupant":
      const { kiosk, unit, kiosks = [], units = [] } = properties || {}
      return transformLocationNamesByFeatures([
        kiosk,
        ...kiosks,
        unit,
        ...units,
      ])
    case "event":
    case "promotion":
      const feature = get(properties, "feature")
      if (isNil(feature)) return ""
      const { feature_type, properties: featureProperties } = feature
      const defaultValue = `${feature_type}.${featureProperties?.category}`
      return getFeatureName(feature, defaultValue)

    case "level":
      const levelFeature = get(properties, "feature")
      return getFeatureName(levelFeature)
    default:
      return null
  }
}

export const getFeatureDetail: GetFeatureDetail = (feature) => {
  const { feature_type } = feature || {}
  switch (feature_type) {
    case "amenity":
      return getAmenityDetail(feature)
    case "unit":
      return getUnitDetail(feature)
    case "kiosk":
      return getKioskDetail(feature)
    case "occupant":
      return getOccupantDetail(feature)
    case "opening":
      return getOpeningDetail(feature)
    case "event":
      return getEventDetail(feature)
    case "promotion":
      return getPromotionDetail(feature)
    case "level":
      return getLevelDetail(feature)
    case "taxonomy":
      return getTaxonomyDetail(feature)
    case "venue":
      return getVenueDetail(feature)
    case "post":
      return getPostDetail(feature)
    case "geolocation":
      return getGeolocationDetail(feature)
    case "section":
      return getSectionDetail(feature)
    case "privilege":
      return getPrivilegeDetail(feature)
    default:
      return null
  }
}

/** Create ListItem Props  */
export const listItemPropsParserFactory: ListItemPropsParserFactoryParams =
  ({ t, hideLevel, hideVenueLogo, newShopConfig }) =>
  (feature) => {
    const { feature_type, id, properties } = feature
    switch (feature_type) {
      case "amenity": {
        const {
          group,
          name,
          venueRef,
          /* category,  */ level,
          locationDescription,
          zone,
          isAd = false,
          isNavigatable = true,
          logo,
          sectionName,
          hasLocation,
        } = getFeatureDetail(feature) as IAmenityDetail
        /* const categoryText = t(category) */
        const floorText =
          !hideLevel && `${t("Level Text", { level: t(level) })}`
        const zoneText = zone && `${t("Zone")} ${t(zone)}`

        const locationDescriptionText =
          locationDescription && t(locationDescription)
        const groupText = group && t(group)
        const categoryIconPath = getIconPathString(
          `${feature_type}.${properties.category}`
        )
        const venueLogo =
          !hideVenueLogo && properties?.venue?.properties?.logo?.url

        return {
          id,
          avatarProps: {
            src: logo,
            icon: { icon_path: categoryIconPath },
          },
          venueLogo,
          venueRef,
          name,
          floorText,
          zoneText,
          groupText,
          locationDescriptionText,
          sectionName,
          isAd,
          isNavigatable,
          hasLocation,
          ...feature,
        }
      }
      case "occupant":
      default: {
        const {
          group,
          name,
          category,
          level,
          zone,
          categoryIconPath,
          categoryIconImage,
          isAd = false,
          boothName,
          venueRef,
          maintenance,
          sectionName,
          hasLocation,
        } = getFeatureDetail(feature) as IOccupantDetail

        const categoryText = category && t(category)
        const floorText =
          !hideLevel && `${t("Level Text", { level: t(level) })}`
        const zoneText = zone && `${t("Zone")} ${t(zone)}`
        const boothNameText = boothName && `${t("Booth")} ${t(boothName)} `
        const groupText = group && t(group)

        const avatarSrc =
          properties?.logo?.formats?.thumbnail?.url || properties?.logo?.url
        const venueLogo =
          !hideVenueLogo && properties?.venue?.properties?.logo?.url

        // TODO: move to prepare fn
        const currentDate = dayjs()
        const periodInDays = _.get(newShopConfig, "metadata.periodInDays", 30)
        const startDate = dayjs(properties?.start_date)
        const isNewShop =
          currentDate.isSameOrAfter(startDate) &&
          currentDate.diff(startDate, "day") <= periodInDays
        const isComingSoon = startDate.isAfter(currentDate)

        return {
          id,
          avatarProps: {
            src: avatarSrc,
            icon: { icon_path: categoryIconPath, image: categoryIconImage },
          },
          venueRef,
          venueLogo,
          name,
          isAd,
          categoryText,
          floorText,
          zoneText,
          boothNameText,
          groupText,
          maintenance,
          sectionName,
          isNewShop,
          isComingSoon,
          hasLocation,
          ...feature,
        }
      }
    }
  }

// Sorting Config Builder
const SortingConfigBuilder = {
  byOrdinal: () => ({
    field: "properties.ordinal",
    order: "desc" as const,
  }),

  byName: (
    language: string,
    { lettersFirst = false }: { lettersFirst?: boolean } = {}
  ): SortingConfigFeatureByName[] => {
    if (lettersFirst) {
      // Single comparator entry — letters-first, digits-last, locale-aware.
      // strategy disambiguates the cache key since JSON.stringify drops `compare`.
      return [
        {
          field: `properties.name.${language}`,
          order: "asc" as const,
          strategy: "nameLettersFirst",
          compare: createLetterNameFirstComparator({
            chosenLanguage: language,
          }),
        },
      ]
    }

    return [
      { field: `properties.name.${language}`, order: "asc" as const },
      { field: "properties.name.en", order: "asc" as const },
    ]
  },
}

// sort config by name
export const createSortingConfigFeatureByName: CreateSortingConfigFeatureByName =
  (chosenLanguage) => {
    return SortingConfigBuilder.byName(chosenLanguage)
  }

// sort config by ordinal and name
export const createSortingConfigByOrdinalAndName: CreateSortingConfigByOrdinalAndName =
  (chosenLanguage) => {
    return [
      SortingConfigBuilder.byOrdinal(),
      ...SortingConfigBuilder.byName(chosenLanguage),
    ]
  }

// sort config: letters-first, digits-last (locale-aware comparator)
export const createSortingConfigFeatureByNameLettersFirst: CreateSortingConfigFeatureByNameLettersFirst =
  (chosenLanguage) => {
    return SortingConfigBuilder.byName(chosenLanguage, { lettersFirst: true })
  }

const DEFAULT_CATEGORY_ICON_CONFIG = { icon_path: null, icon_image: null }
export const getCategoryIcon: GetCategoryIcon = (category) => {
  const { icon_path, icon_image: image } = _.get(
    category,
    "properties",
    DEFAULT_CATEGORY_ICON_CONFIG
  )
  return { image, icon_path }
}

//filter upcoming and current event/promotion
export const filterCurrentFeature = (feature) => {
  const currentTime = dayjs()
  const { properties } = feature
  const startDate = dayjs(properties?.start_date)
  const endDate = dayjs(properties?.end_date)
  const showAlways = !!properties?.show_always

  return (
    showAlways ||
    (currentTime.isAfter(startDate) && currentTime.isBefore(endDate))
  )
}
export const filterUpcomingFeature = (feature) => {
  const currentTime = dayjs()
  const { properties } = feature
  const startDate = dayjs(properties?.start_date)
  const showAlways = !!properties?.show_always
  return showAlways || currentTime.isBefore(startDate)
}

export const getFeatureIDByPath = (path?: string) => {
  if (!path) return null
  const [routePath] = path.split("?")
  return last(routePath.split("/")) || null
}

export const groupItemsByLevel = (items: any[]) => {
  const withLevel = items.filter((i) => i?.properties?.level?.id)
  const withoutLevel = items.filter((i) => !i?.properties?.level?.id)

  const grouped = _(withLevel)
    .groupBy((i) => i.properties.level.id)
    .entries()
    .map(([, groupItems]) => ({
      level: groupItems[0].properties.level,
      items: groupItems,
    }))
    .orderBy(({ level }) => level.properties.ordinal, "desc")
    .value()

  if (withoutLevel.length > 0) {
    grouped.push({
      level: {
        id: "__other__",
        properties: { name: { en: "Other" }, ordinal: -Infinity },
      },
      items: withoutLevel,
    })
  }

  return grouped
}

export const formatLevelName = (level: string) => {
  return level.toUpperCase().endsWith("F") ? level.slice(0, -1) : level
}
